// Picker — the FIELD of the selection family (ADR-080): one engine for every
// list that selects, source-driven. A screen names WHERE the list comes from
// (a source descriptor beside its entity's hook — a vocabulary, a small list,
// the server's bounded lookup) and the picker does the rest through the one
// hook (`useOptions`): nothing loads while closed except the names of what is
// chosen; the list loads on the press; typing matches by the shared rules in
// the browser or asks the server; a FIELD offers what can be picked now (ADR-080
// §5 — choose ≠ filter by): a disabled value is never offered, typed or not, and
// the one already chosen shows marked (an edit keeps what it holds); the chosen
// stay NAMED. The closed face and the hosting are `PickerField` (the desk panel ·
// the phone sheet, bottom for a short list, full for a longer one); the inside is
// `PickerList` — or, for a deep tree (`navigator`), `NavigatorInside`.
//
// THE PARENT DECLARATION (ADR-080 §9): a dependent source — a small list whose
// rows carry a parent (`clustersSource`), a lookup whose rule stands inside a
// container (`assetSource` at a site) — cannot be given to this field without
// `dependsOn: { value, case }`; the type refuses it. The parent is one value, or
// a SET (the countries a project covers — a row belongs while its parent mark is
// one of them). On a parent change:
// `must_belong` keeps what still belongs (a small list, by each row's parent
// mark; a server-backed field clears whole — its picks were by construction
// inside the old container, the server re-checks on Save) and SAYS what it
// cleared (a `role="status"` line under the field that stays until the next
// change, naming the OLD parent); `browse` touches nothing; `dormant` keeps and
// marks the chosen "Dormant" (a setting waiting for its switch) while the parent
// is not the one the value was set under — the mark lifts when it returns; a
// switch names the value it waits for (`awaits`), so a setting SEEDED while it is
// off reads Dormant at once. The guard: an
// EMPTY parent becoming a value never clears (a child is not picked before its
// parent; a seeded edit form is protected); a value → another value or → empty
// clears. No async ask — the clear is deterministic, the one sanctioned effect
// (a sync into the RHF store). RHF: bind through <Controller>
// (FRONTEND-PATTERNS §Forms). A pick-one re-press of the chosen row keeps a
// required field's value (a clearable one clears); the phone face is decided at
// the press and kept while open.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptions, type CodeSource, type DependentSource, type OptionRow, type ServerSource, type SmallSource } from '../../hooks/useOptions.js';
import { PickerField, type PickerChosen, type PickerFieldName } from '../ui/PickerField.js';
import { PickerList, type PickerListProps } from '../ui/PickerList.js';
import type { SheetFace } from '../ui/Sheet.js';
import { NavigatorInside } from './NavigatorInside.js';

export type DependsCase = 'must_belong' | 'browse' | 'dormant';

export interface DependsOn {
  /** The parent field's value as it stands (null = none chosen) — or a SET of values (the countries a project covers):
   *  a row belongs while its parent mark is one of them; an empty set is no parent. */
  value: string | readonly string[] | null;
  /** The parent's NAME — the sentence that says a clear names the OLD parent ("…: it was under Mount Lebanon."). */
  label?: string;
  case: DependsCase;
  /** `dormant` under a SWITCH: the parent value the setting waits for (`'on'`) — the chosen read Dormant whenever the
   *  parent is not it, a value seeded while the switch is off included. Without it the parent of record decides. */
  awaits?: string;
}

interface PickerCommon {
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** A clear-✕ once something is chosen — optional fields and nullable filters only. */
  clearable?: boolean;
  /** The rule in words, under the field. */
  help?: ReactNode;
  className?: string;
  /** False = the picker asks nothing at all (a host that pre-mounts a closed drawer). */
  enabled?: boolean;
  /** The panel opens at mount (open-to-edit; the stories). */
  defaultOpen?: boolean;
  /** Forces the host face (stories; the gate runs at one viewport). */
  face?: 'desk' | 'phone';
  searchPlaceholder?: string;
  /** The row's own look — default: icon · label · description · trailing. */
  renderRow?: PickerListProps['renderRow'];
  /** The heading of a suggestion group — default: the family's words. */
  groupLabel?: PickerListProps['groupLabel'];
  /** The list's last row that ACTS ("Add a new product" — ADR-080 §15's shape); a press runs it and closes a pick-one. */
  tail?: PickerListProps['tail'];
  /** A DEEP tree on the NAVIGATOR (ADR-080 §14 — the source's rows carry `inside`): walked one level at a time, the
   *  fixed second line of the "this level" row ("This area"); the closed face shows the pick's path under its name. */
  navigator?: { thisLevel: string };
  /** The field's name in its form — what a host that opens a drawer ON a field finds the trigger by. */
  name?: string;
}

type PickerValue =
  | { multiple?: false; value: string | null; onChange: (value: string | null) => void }
  | {
      multiple: true;
      value: string[];
      onChange: (value: string[]) => void;
      /** `count` (compact — a filter, a tight space), `tags` (chips with ✕ below the field — a form) or `none` (the chosen
       *  have their own face beside the field — the responsibilities' ordered chain; the box always asks). Default `count`. */
      selectedDisplay?: 'count' | 'tags' | 'none';
      selectedItemsLabel?: string;
    };

/** A dependent source is refused without its declaration (ADR-080 §9). */
type PickerSource = { source: DependentSource; dependsOn: DependsOn } | { source: CodeSource | SmallSource | ServerSource; dependsOn?: DependsOn };

export type PickerProps = PickerCommon & PickerFieldName & PickerValue & PickerSource;

const NONE: readonly string[] = [];

export function Picker(props: PickerProps) {
  const { source, dependsOn, placeholder, required, error, disabled = false, clearable, help, className, enabled = true, defaultOpen = false, face, searchPlaceholder, renderRow, groupLabel, tail, navigator, name: fieldKey } = props;
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(defaultOpen);
  const [text, setText] = useState('');
  // What a clear said — until the next change of the value.
  const [notice, setNotice] = useState<string | null>(null);

  const chosen = useMemo<readonly string[]>(() => (props.multiple ? props.value : props.value ? [props.value] : NONE), [props.multiple, props.value]);
  const parentRaw = dependsOn?.value ?? null;
  // The parent's values (one, or a set — empty = none) and ONE key per parent state: a set's order is not another parent.
  const parentValues = useMemo<readonly string[]>(() => (parentRaw === null ? NONE : typeof parentRaw === 'string' ? [parentRaw] : parentRaw), [parentRaw]);
  const parentValue = parentValues.length === 0 ? null : typeof parentRaw === 'string' ? parentRaw : [...parentValues].sort().join('\u0000');
  // The parent the value was SET UNDER: `dormant` marks the chosen only while the parent differs from it, so a return lifts the mark (ADR-080 §9).
  const [under, setUnder] = useState<string | null>(parentValue);
  // A `none` face shows nothing of the chosen while closed (the host's own list is the face and the host names it — the
  // chain editor), so the closed picker names nothing: no `ids=` batch beside the host's own. Opened, the chosen flow in
  // and lead the list — named from the source's shared rows the host already met.
  const none = props.multiple === true && props.selectedDisplay === 'none';
  // A field offers what can be picked now (ADR-080 §5): never a disabled row, typed or not — the held one stays, marked.
  const options = useOptions(source, { open, text, chosen: none && !open ? NONE : chosen, parent: parentRaw, enabled, purpose: 'field' });
  const fieldName = typeof props.label === 'string' ? props.label : (props.ariaLabel ?? '');

  const commit = (next: readonly string[]) => {
    setNotice(null);
    setUnder(parentValue);
    if (props.multiple) props.onChange([...next]);
    else {
      props.onChange(next[0] ?? null);
      setText('');
    }
  };
  // A pick-one re-press of the chosen row empties the selection: a clearable field clears, a required one keeps its value — the list closes either way (ADR-080 §10: ✕ only when optional).
  const pick = (next: readonly string[]) => {
    if (!props.multiple && next.length === 0 && !clearable) return;
    commit(next);
  };

  // ── the parent declaration — a parent CHANGE is detected DURING RENDER with the previous-value guard
  //    (the house idiom: never setState in an effect); the clear itself is the one sanctioned effect, a sync into the form's store ──
  const [seen, setSeen] = useState<{ value: string | null; label: string | undefined }>({ value: parentValue, label: dependsOn?.label });
  const [clearing, setClearing] = useState<{ seq: number; kept: readonly string[] } | null>(null);
  if (dependsOn && seen.value !== parentValue) {
    const old = seen;
    setSeen({ value: parentValue, label: dependsOn.label });
    if (chosen.length > 0) {
      if (old.value === null) {
        // Empty → a value never clears: a child is not picked before its parent; a seeded edit form keeps its value — a value with
        // NO parent of record ADOPTS this one; one set under a parent keeps it (its mark stays until that parent returns).
        if (under === null) setUnder(parentValue);
      } else if (dependsOn.case === 'must_belong') {
        // A small list keeps what still belongs by each row's parent mark (one of the parent's values when it is a set);
        // a server-backed field clears whole. What survives stands under the new parent.
        const belongs = new Set(parentValues);
        const kept =
          source.kind === 'server'
            ? []
            : chosen.filter((v) => {
                const mark = options.chosenRows.find((r) => r.id === v)?.parent;
                return parentValues.length === 0 ? mark === null : mark != null && belongs.has(mark);
              });
        const dropped = chosen.length - kept.length;
        setUnder(parentValue);
        if (dropped > 0) {
          setNotice(old.label ? t('picker.cleared', { count: dropped, field: fieldName, parent: old.label }) : t('picker.clearedPlain', { count: dropped, field: fieldName }));
          setClearing({ seq: (clearing?.seq ?? 0) + 1, kept });
        }
      }
      // `browse` touches nothing; `dormant` keeps — its mark is derived from `under` below.
    }
  } else if (dependsOn && seen.label !== dependsOn.label) {
    // The parent's NAME may arrive after its value (named by its own read) — remembered for the sentence.
    setSeen({ value: parentValue, label: dependsOn.label });
  }
  useEffect(() => {
    if (!clearing) return;
    if (props.multiple) props.onChange([...clearing.kept]);
    else props.onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per clear (its sequence number), never on a re-render
  }, [clearing?.seq]);

  // Dormant: a setting waiting for its switch — the parent is not the value it awaits, or (no `awaits`) not the one the chosen were set under (ADR-080 §9).
  const dormant = dependsOn?.case === 'dormant' && chosen.length > 0 && (dependsOn.awaits !== undefined ? parentValue !== dependsOn.awaits : under !== parentValue);
  // The chosen, NAMED as far as they are known — "…" until named, "Not available" when the source answered absent;
  // a deep tree's pick carries its PATH under its name (the row's own, ADR-080 §14).
  const named = useMemo<PickerChosen[]>(() => {
    const by = new Map(options.chosenRows.map((r) => [r.id, r] as const));
    return chosen.map((id) => {
      const row = by.get(id);
      if (row) return { id, label: row.selectedLabel ?? row.label, description: navigator ? row.path : undefined, inactive: row.inactive, dormant };
      return options.unavailable.has(id) ? { id, label: id, unavailable: true } : { id, label: '…' };
    });
  }, [chosen, options.chosenRows, options.unavailable, dormant, navigator]);

  // The props' own union guarantees the pairing (a ReactNode label comes with its `ariaLabel`).
  const name = (props.label !== undefined ? { label: props.label, ariaLabel: props.ariaLabel } : { ariaLabel: props.ariaLabel }) as PickerFieldName;
  // The phone face is DECIDED AT THE PRESS and kept while open (ADR-080 §11): a KNOWN short list from the bottom; a searched one — or one whose size is not known yet — full screen, so the face never jumps when a read lands.
  const [phone, setPhone] = useState<{ open: boolean; face: SheetFace }>({ open: false, face: 'full' });
  if (phone.open !== open) setPhone({ open, face: open ? (options.total !== null && !options.searchable ? 'bottom' : 'full') : phone.face });
  const shared = {
    ...name,
    chosen: named,
    open,
    onOpenChange: (o: boolean, typed?: string) => {
      setOpen(o);
      setText(o ? (typed ?? '') : '');
    },
    face,
    phoneFace: phone.face,
    placeholder,
    required,
    error,
    disabled,
    clearable,
    help,
    className,
    name: fieldKey,
    onClear: () => commit(NONE),
  };

  const inside = ({ close, face: hostFace }: { close: () => void; face: 'desk' | 'phone' }) =>
    navigator ? (
      <NavigatorInside
        options={options}
        text={text}
        onTextChange={setText}
        value={chosen}
        onChange={pick}
        multiple={props.multiple === true}
        thisLevelLine={navigator.thisLevel}
        searchPlaceholder={searchPlaceholder}
        searchAutoFocus={options.searchable}
        onPick={props.multiple ? undefined : close}
        ariaLabel={fieldName}
        rowSize={hostFace === 'phone' ? 'touch' : 'default'}
      />
    ) : (
    <PickerList
      rows={options.rows}
      value={chosen}
      onChange={pick}
      multiple={props.multiple === true}
      // The chosen lead the open list in both faces (ADR-080 §6 · §10: "+N more" opens the list, the chosen on top) — the chips are the closed face.
      selected={options.chosenRows}
      searchable={options.searchable}
      text={text}
      onTextChange={setText}
      searchPlaceholder={searchPlaceholder}
      searchAutoFocus={options.searchable}
      groupLabel={groupLabel}
      loading={options.loading}
      searching={options.searching}
      failed={options.failed}
      hasMore={options.hasMore}
      settled={options.settled}
      onPick={props.multiple ? undefined : close}
      tail={tail}
      renderRow={renderRow}
      ariaLabel={fieldName}
      rowSize={hostFace === 'phone' ? 'touch' : 'default'}
    />
    );

  const field = props.multiple ? (
    <PickerField {...shared} multiple selectedDisplay={props.selectedDisplay ?? 'count'} selectedItemsLabel={props.selectedItemsLabel} onRemove={(id) => commit(chosen.filter((v) => v !== id))}>
      {inside}
    </PickerField>
  ) : (
    <PickerField {...shared}>{inside}</PickerField>
  );

  return (
    <div className="w-full">
      {field}
      {/* What a parent change cleared — said, and kept until the next change (ADR-080 §9). The region exists before its text so it is announced. */}
      {dependsOn && (
        <p role="status" aria-live="polite" className={notice ? 'mt-1 text-xs text-[var(--color-text-secondary)]' : 'sr-only'}>
          {notice}
        </p>
      )}
    </div>
  );
}

export type { OptionRow };
